import React, { useState, useEffect, useCallback } from "react";
import { 
  Users, Search, Filter, ArrowUpDown, MessageCircle, ChevronDown, 
  Phone, Mail, Calendar, Star, Clock, MoreHorizontal, RefreshCw,
  UserCheck, UserX, Eye, Send, Plus, FileText, Activity
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Modal, Select, Tag, Button, Input, Card, Statistic, Row, Col, Table, Tooltip, message, Space, Badge, Dropdown, Empty, Spin } from "antd";

const STATUS_CONFIG = {
  nouveau: { color: "blue", label: "Nouveau" },
  contacte: { color: "orange", label: "Contacté" },
  qualifie: { color: "purple", label: "Qualifié" },
  converti: { color: "green", label: "Converti" },
  perdu: { color: "red", label: "Perdu" },
  archive: { color: "default", label: "Archivé" },
};

export default function AdminCrm() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [managers, setManagers] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 0 });
  const [filters, setFilters] = useState({ status: "", search: "", page: 1, limit: 20 });
  const [selectedLead, setSelectedLead] = useState(null);
  const [detailModal, setDetailModal] = useState(false);
  const [assignModal, setAssignModal] = useState(false);
  const [commentModal, setCommentModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [assignManagerId, setAssignManagerId] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/crm/leads", { params: filters });
      if (res.success) {
        setLeads(res.data.leads || []);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      message.error("Erreur lors du chargement des leads");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get("/admin/crm/stats");
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error("Stats error:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadManagers = useCallback(async () => {
    try {
      const res = await api.get("/admin/crm/managers");
      if (res.success) setManagers(res.data);
    } catch (err) {
      console.error("Managers error:", err);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadStats(); loadManagers(); }, [loadStats, loadManagers]);

  const handleAssign = async () => {
    if (!assignManagerId || !selectedLead) return;
    try {
      const res = await api.put(`/admin/crm/leads/${selectedLead.id}/assign`, { manager_id: assignManagerId });
      if (res.success) {
        message.success("Lead assigné avec succès");
        setAssignModal(false);
        loadLeads();
      }
    } catch (err) {
      message.error("Erreur lors de l'assignation");
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim() || !selectedLead) return;
    try {
      const res = await api.post(`/crm/leads/${selectedLead.id}/comments`, { comment: commentText });
      if (res.success) {
        message.success("Commentaire ajouté");
        setCommentText("");
        setCommentModal(false);
        // Reload lead detail
        loadLeadDetail(selectedLead.id);
      }
    } catch (err) {
      message.error("Erreur lors de l'ajout du commentaire");
    }
  };

  const loadLeadDetail = async (id) => {
    try {
      const res = await api.get(`/admin/crm/leads/${id}`);
      if (res.success) setSelectedLead(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const openDetail = async (lead) => {
    await loadLeadDetail(lead.id);
    setDetailModal(true);
  };

  const handleResendWhatsApp = async (leadId) => {
    try {
      const res = await api.post(`/admin/crm/leads/${leadId}/resend-whatsapp`);
      if (res.success) message.success("Message WhatsApp renvoyé");
    } catch (err) {
      message.error("Erreur lors du renvoi");
    }
  };

  const handleStatusChange = async (leadId, status) => {
    try {
      const res = await api.put(`/admin/crm/leads/${leadId}/status`, { status });
      if (res.success) {
        message.success("Statut mis à jour");
        loadLeads();
        loadStats();
      }
    } catch (err) {
      message.error("Erreur");
    }
  };

  const columns = [
    { 
      title: "Client", 
      dataIndex: "nom", 
      key: "nom",
      render: (_, record) => (
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-black">
            {record.prenom?.charAt(0)}{record.nom?.charAt(0)}
          </div>
          <div>
            <div className="text-sm font-bold text-white">{record.prenom} {record.nom}</div>
            <div className="text-xs text-slate-500">{record.email}</div>
          </div>
        </div>
      ),
    },
    { 
      title: "Contact", 
      key: "contact",
      render: (_, record) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs text-slate-400"><Phone size={11} /> {record.telephone}</div>
          <div className="flex items-center gap-1 text-xs text-slate-400"><Mail size={11} /> {record.email}</div>
        </div>
      ),
    },
    { 
      title: "Expérience", 
      dataIndex: "trading_experience", 
      key: "trading_experience",
      render: (val) => (
        <Tag color={val === 'debutant' ? 'blue' : val === 'intermediaire' ? 'orange' : val === 'avance' ? 'purple' : 'green'} className="text-xs">
          {val || 'N/A'}
        </Tag>
      ),
    },
    { 
      title: "Manager", 
      dataIndex: "manager_name", 
      key: "manager_name",
      render: (name) => name ? <span className="text-xs text-slate-300">{name}</span> : <span className="text-xs text-slate-600">—</span>,
    },
    { 
      title: "Statut", 
      dataIndex: "status", 
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 120 }}
          onChange={(val) => handleStatusChange(record.id, val)}
          className="status-select"
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Select.Option key={key} value={key}>
              <Badge status={cfg.color} text={cfg.label} />
            </Select.Option>
          ))}
        </Select>
      ),
    },
    { 
      title: "Date", 
      dataIndex: "created_at", 
      key: "created_at",
      render: (date) => (
        <span className="text-xs text-slate-400">{new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
      ),
    },
    { 
      title: "Actions", 
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="Voir détails">
            <Button type="text" icon={<Eye size={14} />} onClick={() => openDetail(record)} className="text-slate-400 hover:text-white" size="small" />
          </Tooltip>
          <Tooltip title="Assigner un manager">
            <Button type="text" icon={<UserCheck size={14} />} onClick={() => { setSelectedLead(record); setAssignModal(true); }} className="text-slate-400 hover:text-emerald-400" size="small" />
          </Tooltip>
          <Tooltip title="Renvoyer WhatsApp">
            <Button type="text" icon={<Send size={14} />} onClick={() => handleResendWhatsApp(record.id)} className="text-slate-400 hover:text-emerald-400" size="small" />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>CRM - Gestion des Leads</h1>
          <p className="text-slate-400 text-sm mt-1">Gérez vos prospects et assignez-les aux managers</p>
        </div>
        <Space>
          <Button icon={<FileText size={14} />} onClick={() => navigate('/admin/crm/questions')}>
            Gérer les Questions
          </Button>
          <Button type="primary" icon={<RefreshCw size={14} />} onClick={() => { loadLeads(); loadStats(); }}>
            Actualiser
          </Button>
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={[12, 12]}>
        {[
          { key: 'total', label: 'Total Leads', color: 'indigo' },
          { key: 'nouveau', label: 'Nouveaux', color: 'blue' },
          { key: 'contacte', label: 'Contactés', color: 'orange' },
          { key: 'qualifie', label: 'Qualifiés', color: 'purple' },
          { key: 'converti', label: 'Convertis', color: 'green' },
          { key: 'today_leads', label: "Aujourd'hui", color: 'emerald' },
        ].map(({ key, label, color }) => (
          <Col xs={12} sm={8} md={4} key={key}>
            <Card className="bg-slate-900 border-white/5" size="small">
              <Statistic 
                title={<span className="text-slate-400 text-xs font-bold uppercase tracking-wider">{label}</span>}
                value={stats?.[key] || 0}
                loading={statsLoading}
                valueStyle={{ color: '#e2e8f0', fontSize: 24, fontWeight: 800 }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Input 
          placeholder="Rechercher par nom, email, téléphone..."
          prefix={<Search size={14} className="text-slate-500" />}
          value={filters.search}
          onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
          className="w-72 bg-slate-900 border-white/10"
        />
        <Select
          placeholder="Statut"
          value={filters.status || undefined}
          onChange={(val) => setFilters(prev => ({ ...prev, status: val || '', page: 1 }))}
          allowClear
          className="w-36"
        >
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <Select.Option key={key} value={key}>{cfg.label}</Select.Option>
          ))}
        </Select>
      </div>

      {/* Table */}
      <Card className="bg-slate-900 border-white/5" bodyStyle={{ padding: 0 }}>
        <Table 
          columns={columns}
          dataSource={leads}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.page,
            total: pagination.total,
            pageSize: filters.limit,
            onChange: (page) => setFilters(prev => ({ ...prev, page })),
            showTotal: (total) => `${total} leads`,
          }}
          className="crm-table"
          locale={{ emptyText: <Empty description="Aucun lead trouvé" /> }}
        />
      </Card>

      {/* Detail Modal */}
      <Modal
        title={null}
        open={detailModal}
        onCancel={() => setDetailModal(false)}
        footer={null}
        width={700}
        className="crm-detail-modal"
      >
        {selectedLead && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-indigo-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-lg font-black">
                  {selectedLead.prenom?.charAt(0)}{selectedLead.nom?.charAt(0)}
                </div>
                <div>
                  <h3 className="text-lg font-black text-white">{selectedLead.prenom} {selectedLead.nom}</h3>
                  <Badge status={STATUS_CONFIG[selectedLead.status]?.color} text={STATUS_CONFIG[selectedLead.status]?.label} />
                </div>
              </div>
              <Space>
                <Button size="small" icon={<Send size={12} />} onClick={() => handleResendWhatsApp(selectedLead.id)}>Renvoyer WhatsApp</Button>
                <Button size="small" icon={<UserCheck size={12} />} onClick={() => setAssignModal(true)}>Assigner</Button>
              </Space>
            </div>

            {/* Infos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Contact</div>
                <div className="space-y-2 text-sm text-slate-300">
                  <p className="flex items-center gap-2"><Phone size={13} className="text-slate-500" /> {selectedLead.telephone}</p>
                  <p className="flex items-center gap-2"><Mail size={13} className="text-slate-500" /> {selectedLead.email}</p>
                  <p className="flex items-center gap-2"><Calendar size={13} className="text-slate-500" /> {new Date(selectedLead.created_at).toLocaleDateString('fr-FR')}</p>
                  <p className="flex items-center gap-2"><MessageCircle size={13} className="text-slate-500" /> {selectedLead.contact_method}</p>
                </div>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Profil</div>
                <div className="space-y-2 text-sm text-slate-300">
                  <p><span className="text-slate-500">Expérience:</span> {selectedLead.trading_experience}</p>
                  <p><span className="text-slate-500">Domaine:</span> {selectedLead.domaine_travail || '—'}</p>
                  <p><span className="text-slate-500">Manager:</span> {selectedLead.manager_name || 'Non assigné'}</p>
                  <p><span className="text-slate-500">Source:</span> {selectedLead.source}</p>
                </div>
              </div>
            </div>

            {/* Description expérience */}
            {selectedLead.experience_description && (
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Description de l'expérience</div>
                <p className="text-sm text-slate-300 italic">"{selectedLead.experience_description}"</p>
              </div>
            )}

            {/* WhatsApp Message */}
            {selectedLead.whatsapp_message && (
              <div className="p-4 bg-emerald-500/5 border border-emerald-500/10 rounded-xl">
                <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-2 flex items-center gap-2">
                  <MessageCircle size={12} /> Message WhatsApp envoyé
                </div>
                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-sans">{selectedLead.whatsapp_message}</pre>
              </div>
            )}

            {/* Commentaires */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Commentaires</div>
                <Button size="small" icon={<Plus size={12} />} onClick={() => setCommentModal(true)}>Ajouter</Button>
              </div>
              {selectedLead.manager_comments?.length > 0 ? (
                <div className="space-y-2">
                  {selectedLead.manager_comments.map((c) => (
                    <div key={c.id} className="p-3 bg-slate-800/30 rounded-xl">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-300">{c.username}</span>
                        <span className="text-[10px] text-slate-600">{new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-xs text-slate-400">{c.comment}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-600 italic">Aucun commentaire</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        title="Assigner un manager"
        open={assignModal}
        onCancel={() => setAssignModal(false)}
        onOk={handleAssign}
        okText="Assigner"
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Sélectionner un manager"
          value={assignManagerId}
          onChange={setAssignManagerId}
        >
          {managers.map((m) => (
            <Select.Option key={m.id} value={m.id}>
              {m.username} ({m.email}) — {m.active_leads || 0} leads actifs
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Comment Modal */}
      <Modal
        title="Ajouter un commentaire"
        open={commentModal}
        onCancel={() => setCommentModal(false)}
        onOk={handleAddComment}
        okText="Ajouter"
      >
        <Input.TextArea 
          rows={3}
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Écrire un commentaire..."
        />
      </Modal>

      <style>{`
        .crm-table .ant-table-thead > tr > th {
          background: #0f172a !important;
          border-bottom: 1px solid rgba(255,255,255,0.06) !important;
          color: #94a3b8 !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 12px 16px !important;
        }
        .crm-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(255,255,255,0.04) !important;
          padding: 12px 16px !important;
          background: transparent !important;
        }
        .crm-table .ant-table-tbody > tr:hover > td {
          background: rgba(99,102,241,0.04) !important;
        }
        .status-select .ant-select-selector {
          background: transparent !important;
          border: 1px solid rgba(255,255,255,0.1) !important;
        }
        .crm-detail-modal .ant-modal-content {
          background: #1e293b !important;
          border: 1px solid rgba(255,255,255,0.1) !important;
        }
      `}</style>
    </div>
  );
}
